import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { dbService } from '../../services/firebase';
import { YachtRoute } from '../../types';
import { X, Compass, Image as ImageIcon, MapPin, Calendar, Plus, Trash2, Check } from 'lucide-react';

interface RouteFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  routeToEdit: YachtRoute | null;
}

export const RouteFormModal: React.FC<RouteFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  routeToEdit,
}) => {
  const { activeSite } = useSite();
  const [formData, setFormData] = useState<Partial<YachtRoute>>({
    name: '',
    description: '',
    durationDays: 7,
    departurePort: 'Göcek',
    arrivalPort: 'Göcek',
    coverImage: 'https://images.unsplash.com/photo-1510312305653-8ed496efae75?auto=format&fit=crop&w=1200&q=80',
    highlightBays: ['Bedri Rahmi Koyu', 'Kleopatra Hamamı', 'Tersane Adası'],
    isFeatured: true,
  });

  const [newBay, setNewBay] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (routeToEdit) {
      setFormData(routeToEdit);
    } else {
      setFormData({
        name: '',
        description: '',
        durationDays: 7,
        departurePort: 'Göcek',
        arrivalPort: 'Göcek',
        coverImage: 'https://images.unsplash.com/photo-1510312305653-8ed496efae75?auto=format&fit=crop&w=1200&q=80',
        highlightBays: ['Bedri Rahmi Koyu', 'Kleopatra Hamamı', 'Tersane Adası'],
        isFeatured: true,
        isStarter: false,
      });
    }
  }, [routeToEdit, isOpen]);

  if (!isOpen || !activeSite) return null;

  const handleAddBay = () => {
    if (!newBay.trim()) return;
    const current = formData.highlightBays || [];
    setFormData({ ...formData, highlightBays: [...current, newBay.trim()] });
    setNewBay('');
  };

  const handleRemoveBay = (index: number) => {
    const current = formData.highlightBays || [];
    setFormData({ ...formData, highlightBays: current.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name?.trim()) return;
    setSaving(true);
    try {
      await dbService.saveRoute(activeSite.id, formData);
      onSave();
      onClose();
    } catch (err) {
      console.error('Save route error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in select-none">
      <div className="bg-white rounded-3xl border border-slate-200 w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-600 flex items-center justify-center">
              <Compass className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                {routeToEdit ? 'Mavi Tur Rotasını Düzenle' : 'Yeni Mavi Tur Rotası Ekle'}
              </h2>
              <p className="text-xs text-slate-500">
                Kiralama müşterileriniz için popüler koylar ve durak planı oluşturun.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Rota Adı */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Rota Başlığı *
            </label>
            <input
              type="text"
              required
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Örn: Göcek & Fethiye 12 Adalar Mavi Turu"
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            />
          </div>

          {/* Açıklama */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Rota Açıklaması & Deneyim Özeti
            </label>
            <textarea
              rows={3}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Rota boyunca gezilecek tarihi koylar, berrak sular ve özel restoranlar hakkında bilgi..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none resize-none"
            />
          </div>

          {/* Süre & Limanlar */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Süre (Gün)
              </label>
              <input
                type="number"
                min={1}
                max={30}
                value={formData.durationDays || 7}
                onChange={(e) => setFormData({ ...formData, durationDays: Number(e.target.value) })}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Kalkış Limanı
              </label>
              <input
                type="text"
                value={formData.departurePort || ''}
                onChange={(e) => setFormData({ ...formData, departurePort: e.target.value })}
                placeholder="Örn: Göcek Marina"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Varış Limanı
              </label>
              <input
                type="text"
                value={formData.arrivalPort || ''}
                onChange={(e) => setFormData({ ...formData, arrivalPort: e.target.value })}
                placeholder="Örn: Göcek Marina"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 outline-none"
              />
            </div>
          </div>

          {/* Kapak Görseli */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Kapak Fotoğrafı URL'si
            </label>
            <div className="flex gap-2">
              <input
                type="url"
                value={formData.coverImage || ''}
                onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
                placeholder="https://..."
                className="flex-1 px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 outline-none"
              />
            </div>
            {formData.coverImage && (
              <div className="mt-2 h-28 w-full rounded-xl overflow-hidden border border-slate-200 relative bg-slate-100">
                <img
                  src={formData.coverImage}
                  alt="Kapak önizleme"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>
            )}
          </div>

          {/* Öne Çıkan Koylar & Duraklar */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Öne Çıkan Koylar & Duraklar
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newBay}
                onChange={(e) => setNewBay(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddBay();
                  }
                }}
                placeholder="Yeni koy ekle (Örn: Sarsala Koyu)"
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-900 text-xs focus:border-cyan-500 outline-none"
              />
              <button
                type="button"
                onClick={handleAddBay}
                className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                Ekle
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {formData.highlightBays?.map((bay, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-cyan-50 text-cyan-800 text-xs font-medium border border-cyan-200"
                >
                  <MapPin className="w-3 h-3 text-cyan-500" />
                  <span>{bay}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveBay(idx)}
                    className="text-cyan-400 hover:text-rose-500 ml-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Öne Çıkan Rota Switch */}
          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="route-featured"
              checked={formData.isFeatured || false}
              onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
              className="w-4 h-4 rounded text-cyan-600 focus:ring-cyan-500 border-slate-300"
            />
            <label htmlFor="route-featured" className="text-xs font-semibold text-slate-700 cursor-pointer">
              Ana sayfada ve rota vitrininde öne çıkarılsın
            </label>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-200 text-xs font-bold transition"
          >
            Vazgeç
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !formData.name?.trim()}
            className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold shadow-lg shadow-cyan-600/20 transition disabled:opacity-50 flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            <span>{saving ? 'Kaydediliyor...' : 'Rotayı Kaydet'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
